import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import { Color } from "@tiptap/extension-color";
import Document from "@tiptap/extension-document";
import Dropcursor from "@tiptap/extension-dropcursor";
import { FontFamily } from "@tiptap/extension-font-family";
import Gapcursor from "@tiptap/extension-gapcursor";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import HistoryExt from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Strike from "@tiptap/extension-strike";
import { TableKit } from "@tiptap/extension-table";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { type Extensions, type Node } from "@tiptap/react";
import { Blue9 } from "styles/antTheme/Colors";

import { filterOutFalsy } from "utilities/array/array";

import EmojiExtension from "commonComponents/btWrappers/editor/core/extensions/custom/emoji/EmojiExtension";
import EntityLinkExtension from "commonComponents/btWrappers/editor/core/extensions/custom/entityLink/EntityLinkExtension";
import FontSizeExtension from "commonComponents/btWrappers/editor/core/extensions/custom/FontSizeExtension";
import ImageExtension from "commonComponents/btWrappers/editor/core/extensions/custom/image/ImageExtension";
import IndentParagraphExtension from "commonComponents/btWrappers/editor/core/extensions/custom/IndentParagraphExtension";
import {
    MentionUserExtension,
    userSuggestion,
} from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserExtension";
import OverrideTopLevelEnterKeyExtension from "commonComponents/btWrappers/editor/core/extensions/custom/OverrideTopLevelEnterKeyExtension";
import { TemplateVariableExtension } from "commonComponents/btWrappers/editor/core/extensions/custom/templateVariable/TemplateVariableExtension";
import TitleExtension from "commonComponents/btWrappers/editor/core/extensions/custom/TitleExtension";
import {
    type IBaseExtensionConfig,
    type IBlockquoteExtensionConfig,
    type IBoldExtensionConfig,
    type IEntityLinkExtensionConfig,
    type IHeadingExtensionConfig,
    type IImageExtensionConfig,
    type ILinkExtensionConfig,
    type IListExtensionConfig,
    type IMentionUserExtensionConfig,
    type IOverrideTopLevelEnterKeyExtensionConfig,
    type ITemplateVariableExtensionConfig,
} from "commonComponents/btWrappers/editor/core/extensions/ExtensionProvider.types";
import { supportedFontSizes } from "commonComponents/btWrappers/editor/editor.types";

/**
 * This utility extends the passed extension to add an additional group to the extension
 */
const withAdditionalGroups = <NodeOptions, NodeStorage>(
    extension: Node<NodeOptions, NodeStorage>,
    groups: string[],
) => {
    return extension.extend({
        group() {
            let currentGroups = "";
            if (this.parent) {
                if (typeof this.parent === "string") {
                    currentGroups = this.parent;
                } else {
                    currentGroups = this.parent() ?? "";
                }
            }

            const newGroups = [currentGroups, ...groups];
            return newGroups.join(" ");
        },
    });
};
const bulletableGroup = "bulletable";

export class FeatureSetBuilder {
    private constructor() {}

    private baseExts: Extensions;
    static create(config: IBaseExtensionConfig) {
        const featureSetBuilder = new FeatureSetBuilder();
        featureSetBuilder.baseExts = [
            Document.extend(config.document?.extend),
            withAdditionalGroups(Paragraph.extend(config.paragraph?.extend), [bulletableGroup]),
            Text,
            HistoryExt,
            Placeholder.configure({
                placeholder: "",
                showOnlyCurrent: false,
                ...config.placeholder?.configure,
            }),
            Dropcursor.configure({
                width: 2,
                color: Blue9,
            }),
        ];
        return featureSetBuilder;
    }

    private indentExt: typeof IndentParagraphExtension | undefined;
    public withIndentableParagraphs = () => {
        this.indentExt = IndentParagraphExtension;
        return this;
    };

    private headingExt: typeof Heading | undefined;
    public withHeading = (config: IHeadingExtensionConfig) => {
        this.headingExt = Heading.extend(config.heading?.extend).configure({
            ...config.heading?.configure,
            levels: [1, 2, 3, 4],
        });
        return this;
    };

    private boldExt: typeof Bold | undefined;
    public withBold = (config: IBoldExtensionConfig) => {
        this.boldExt = Bold.extend(config.bold?.extend).configure(config.bold?.configure);
        return this;
    };

    private italicExt: typeof Italic | undefined;
    public withItalic = () => {
        this.italicExt = Italic;
        return this;
    };

    private underlineExt: typeof Underline | undefined;
    public withUnderline = () => {
        this.underlineExt = Underline;
        return this;
    };

    private strikeExt: typeof Strike | undefined;
    public withStrike = () => {
        this.strikeExt = Strike;
        return this;
    };

    private hardbreakExt: typeof HardBreak | undefined;
    public withHardbreak = () => {
        this.hardbreakExt = HardBreak.extend({ marks: "" });
        return this;
    };

    private listItemExt: typeof ListItem | undefined;
    private listExts: Extensions = [];
    public withLists = (config: IListExtensionConfig) => {
        this.listExts = [
            withAdditionalGroups(BulletList, [bulletableGroup]).configure({
                HTMLAttributes: {
                    class: "bulletList",
                },
                ...config.bulletList?.configure,
            }),
            withAdditionalGroups(OrderedList, [bulletableGroup]).configure({
                HTMLAttributes: {
                    class: "orderedList",
                },
                ...config.orderedList?.configure,
            }),
        ];
        this.listItemExt = ListItem.extend({
            content: `paragraph ${bulletableGroup}*`,
        });
        return this;
    };

    private titleExt: typeof TitleExtension | undefined;
    public withTitle = () => {
        this.titleExt = TitleExtension;
        return this;
    };

    private linkExt: typeof Link | undefined;
    public withLink = (config: ILinkExtensionConfig) => {
        this.linkExt = Link.extend({
            ...config.link?.extend,
            inclusive: false,
        }).configure({
            ...config.link?.configure,
            openOnClick: false,
            autolink: true,
            linkOnPaste: true,
        });
        return this;
    };

    private entityLinkExt: typeof EntityLinkExtension | undefined;
    public withEntityLink = (config: IEntityLinkExtensionConfig) => {
        this.entityLinkExt = EntityLinkExtension.configure({
            ...config.entityLink?.configure,
        });
        return this;
    };

    private gapcursorExt: typeof Gapcursor | undefined;
    private imageExt: typeof ImageExtension | undefined;
    public withImage = (config: IImageExtensionConfig) => {
        this.gapcursorExt = Gapcursor;
        this.imageExt = ImageExtension.configure({
            allowBase64: true,
            ...config.image?.configure,
            HTMLAttributes: {
                class: "image",
            },
        });
        return this;
    };

    private textAlignExt: typeof TextAlign | undefined;
    public withTextAlign = () => {
        this.textAlignExt = TextAlign.configure({
            // types: types are defined in the `build` function because we need to know what extensions are enabled
            // otherwise the text align will be disabled due to how it works
            alignments: ["left", "right", "center"],
        });
        return this;
    };

    private textStyleExt: typeof TextStyle | undefined;
    private fontSizeExt: typeof FontSizeExtension | undefined;
    public withFontSize = () => {
        this.textStyleExt = TextStyle;
        this.fontSizeExt = FontSizeExtension.configure({
            sizes: supportedFontSizes,
        });
        return this;
    };

    private highlightExt: typeof Highlight | undefined;
    public withHighlight = () => {
        this.highlightExt = Highlight.configure({
            HTMLAttributes: {
                class: "highlightedText",
            },
        });
        return this;
    };

    private fontFamilyExt: typeof FontFamily | undefined;
    public withFontFamily = () => {
        // FontFamily depends on TextStyle. Ensure TextStyle is registered, but don't clobber if
        // another builder (e.g., withFontSize) has already configured it.
        this.textStyleExt = this.textStyleExt ?? TextStyle;
        this.fontFamilyExt = FontFamily;
        return this;
    };

    private textColorExt: typeof Color | undefined;
    public withTextColor = () => {
        // Color depends on TextStyle. Same dependency-init pattern as withFontFamily.
        this.textStyleExt = this.textStyleExt ?? TextStyle;
        this.textColorExt = Color;
        return this;
    };

    private tableExt: typeof TableKit | undefined;
    public withTable = () => {
        this.tableExt = TableKit.configure({
            table: {
                resizable: true,
                HTMLAttributes: {
                    class: "editorTable",
                },
            },
        });
        return this;
    };

    private blockquoteExt: typeof Blockquote | undefined;
    public withBlockquote = (config: IBlockquoteExtensionConfig) => {
        this.gapcursorExt = Gapcursor;
        this.blockquoteExt = Blockquote.configure({
            HTMLAttributes: {
                class: "blockquote",
            },
            ...config.blockquote?.configure,
        });
        return this;
    };

    private emojiExt: typeof EmojiExtension | undefined;
    public withEmoji = () => {
        this.emojiExt = EmojiExtension;
        return this;
    };

    private overrideTopLevelEnterKeyExt: typeof OverrideTopLevelEnterKeyExtension | undefined;
    public withOverrideTopLevelEnterKey = (config: IOverrideTopLevelEnterKeyExtensionConfig) => {
        this.overrideTopLevelEnterKeyExt = OverrideTopLevelEnterKeyExtension.configure({
            ...config.overrideTopLevelEnterKey?.configure,
        });
        return this;
    };

    private mentionUserExt: typeof MentionUserExtension | undefined;
    public withMentionUser = (config: IMentionUserExtensionConfig) => {
        if (config.mentionUser) {
            this.mentionUserExt = MentionUserExtension.configure({
                suggestion: (editor) => userSuggestion(config)(editor),
                unsupportedEntityType: config.mentionUser?.configure?.unsupportedEntityType,
            });
        }
        return this;
    };

    private templateVariableExt: typeof TemplateVariableExtension | undefined;
    public withTemplateVariable = (config: ITemplateVariableExtensionConfig) => {
        // Gated on config: only consumers that supply a variable set get the extension, so every
        // other editor (chat, standard, etc.) is unaffected.
        if (config.templateVariable?.sources.length) {
            this.templateVariableExt = TemplateVariableExtension.configure({
                sources: config.templateVariable.sources,
                readOnlyMode: config.templateVariable.readOnlyMode ?? "replaceVariables",
            });
        }
        return this;
    };

    public build = (): Extensions => {
        // Resolve conflicting extensions
        let listItemExt = this.listItemExt;
        if (this.imageExt && listItemExt) {
            listItemExt = listItemExt.extend({
                content: `(${Paragraph.name}|${this.imageExt.name}) block*`,
            });
        }

        if (this.textAlignExt) {
            const types: (string | undefined)[] = [
                this.headingExt?.name,
                Paragraph.name,
                this.indentExt?.name,
                this.imageExt?.name,
            ];
            this.textAlignExt = this.textAlignExt.configure({
                types: filterOutFalsy(types),
            });
        }

        if (this.emojiExt && !this.textStyleExt) {
            this.emojiExt = this.emojiExt.extend({
                marks: "",
            });
        } else if (this.emojiExt && this.textStyleExt) {
            this.emojiExt = this.emojiExt.extend({
                marks: this.textStyleExt.name,
            });
        }

        // In general, order of this array doesn't matter except for a select few extensions.
        // DO NOT attempt to alphabetize
        const extensions = [
            ...this.baseExts,
            this.indentExt,
            this.headingExt,
            this.boldExt,
            this.italicExt,
            this.underlineExt,
            this.strikeExt,
            this.hardbreakExt,
            this.overrideTopLevelEnterKeyExt, // needs to be after hardBreak because we override Shift-Enter and use the hardBreak command
            ...this.listExts,
            listItemExt,
            this.titleExt,
            this.linkExt,
            this.entityLinkExt,
            this.imageExt,
            this.textAlignExt,
            this.textStyleExt,
            this.fontSizeExt,
            this.fontFamilyExt,
            this.textColorExt,
            this.highlightExt,
            this.blockquoteExt,
            this.emojiExt,
            this.gapcursorExt,
            this.mentionUserExt,
            this.templateVariableExt,
            this.tableExt,
        ];

        return filterOutFalsy(extensions);
    };
}
